import { useEffect, useRef, useState } from 'react';

import { LoggerService } from 'services';

export type IVSPlayerProps = {
  playbackUrl: string;
  retryOnPause?: boolean;
  active?: boolean;
  onAutoplayBlocked?(): void;
};

const isSupported = IVSPlayer.isPlayerSupported;

const useIVSPlayer = ({ playbackUrl, retryOnPause = false, active = false, onAutoplayBlocked }: IVSPlayerProps) => {
  const player = useRef<IVSPlayer.MediaPlayer | null>(null);
  const videoEl = useRef<HTMLVideoElement>(null);
  const retryInterval = useRef<NodeJS.Timeout | null>(null);

  const [, setError] = useState<IVSPlayer.PlayerError | null>(null);
  const [playerState, setPlayerState] = useState<IVSPlayer.PlayerState | null>(null);

  const isLoading = playerState !== IVSPlayer.PlayerState.PLAYING;

  useEffect(() => {
    if (!isSupported) return () => {};

    const onStateChange = () => {
      if (!player.current) return;

      const newPlayerState = player.current?.getState();

      setPlayerState(newPlayerState);
      setError(null);
    };

    const onError = (e: IVSPlayer.PlayerError) => {
      LoggerService.log('PLAYBACK_ERROR', e);
      setError(e);
    };

    const onAudioBlocked = () => {
      onAutoplayBlocked?.();
    };

    const onPlaybackBlocked = () => {
      onAutoplayBlocked?.();
    };

    // IVSPlayer.create requires config, but in fact, config is only needed when IVS SDK used locally with bundler
    // But, we are using SDK though CDN in index.html, not locally
    // REFERENCE: https://aws.github.io/amazon-ivs-player-docs/1.8.0/web/interfaces/playerconfig.html
    // @ts-ignore
    player.current = IVSPlayer.create();
    player.current.attachHTMLVideoElement(videoEl.current as HTMLVideoElement);
    player.current.setInitialBufferDuration(5);

    const { ENDED, PLAYING, READY, IDLE, BUFFERING } = IVSPlayer.PlayerState;
    const { ERROR, AUDIO_BLOCKED, PLAYBACK_BLOCKED } = IVSPlayer.PlayerEventType;

    load();

    player.current.addEventListener(IDLE, onStateChange);
    player.current.addEventListener(BUFFERING, onStateChange);
    player.current.addEventListener(READY, onStateChange);
    player.current.addEventListener(PLAYING, onStateChange);
    player.current.addEventListener(ENDED, onStateChange);
    player.current.addEventListener(ERROR, onError);
    player.current.addEventListener(AUDIO_BLOCKED, onAudioBlocked);
    player.current.addEventListener(PLAYBACK_BLOCKED, onPlaybackBlocked);

    return () => {
      player.current?.removeEventListener(BUFFERING, onStateChange);
      player.current?.removeEventListener(READY, onStateChange);
      player.current?.removeEventListener(PLAYING, onStateChange);
      player.current?.removeEventListener(ENDED, onStateChange);
      player.current?.removeEventListener(ERROR, onError);
      player.current?.removeEventListener(AUDIO_BLOCKED, onAudioBlocked);
      player.current?.removeEventListener(PLAYBACK_BLOCKED, onPlaybackBlocked);

      player.current?.delete();
      player.current = null;

      setPlayerState(null);
    };
  }, []);

  useEffect(() => {
    if (!active) return () => {};

    const { ENDED, IDLE, READY } = IVSPlayer.PlayerState;

    switch (playerState) {
      case null:
      case IDLE:
      case ENDED: {
        if (retryOnPause && !retryInterval.current) {
          retryInterval.current = setInterval(() => load(), 5_000);
        }
        break;
      }

      case READY: {
        if (retryOnPause) {
          play();
        }

        break;
      }
    }

    return () => {
      retryInterval.current && clearInterval(retryInterval.current);
      retryInterval.current = null;
    };
  }, [retryOnPause, active, playerState]);

  useEffect(() => {
    if (active && retryOnPause) play();

    return () => pause();
  }, [active, retryOnPause]);

  const load = () => {
    if (!player.current) return;

    player.current.load(playbackUrl);
  };

  const play = () => {
    player.current?.setMuted(false);
    player.current?.play();
  };

  const pause = () => {
    player.current?.setMuted(true);
    player.current?.pause();
  };

  return {
    player,
    playerState,
    videoEl,
    isLoading,
    load,
    play,
    pause
  };
};

export default useIVSPlayer;
